import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Beef, Gift, Phone, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const API = `${BACKEND_URL}/api`;

export default function OffersPage() {
  const [offers, setOffers] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();

    const refreshWhenVisible = () => {
      if (document.visibilityState === 'visible') fetchData(true);
    };

    const interval = window.setInterval(refreshWhenVisible, 15000);
    window.addEventListener('focus', refreshWhenVisible);
    window.addEventListener('online', refreshWhenVisible);
    document.addEventListener('visibilitychange', refreshWhenVisible);

    return () => {
      window.clearInterval(interval);
      window.removeEventListener('focus', refreshWhenVisible);
      window.removeEventListener('online', refreshWhenVisible);
      document.removeEventListener('visibilitychange', refreshWhenVisible);
    };
  }, []);

  const fetchData = async (silent = false) => {
    try {
      const timestamp = Date.now();
      const requestConfig = {
        headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' }
      };
      const [offersRes, settingsRes] = await Promise.all([
        axios.get(`${API}/offers?t=${timestamp}`, requestConfig),
        axios.get(`${API}/settings?t=${timestamp}`, requestConfig)
      ]);
      
      setOffers(offersRes.data);
      setSettings(settingsRes.data);
    } catch (error) {
      if (!silent) toast.error('Errore nel caricamento delle offerte');
    } finally {
      setLoading(false);
    }
  };

  const handleCall = () => {
    if (settings?.phone_number) {
      window.location.href = `tel:${settings.phone_number}`;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9F7F2] flex items-center justify-center">
        <div className="text-[#5D1822] text-xl">Caricamento...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F7F2]" data-testid="offers-page">
      {/* Header */}
      <header className="bg-white border-b border-[#E5E5E5]">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <Link to="/" className="flex items-center gap-3">
            <Beef className="w-8 h-8 text-[#5D1822]" />
            <h1 className="text-2xl font-serif font-bold text-[#5D1822]">
              {settings?.business_name || 'Macelleria Borgese'}
            </h1>
          </Link>
          <button
            onClick={handleCall}
            className="flex items-center gap-2 btn-primary"
            data-testid="call-button"
          >
            <Phone size={20} />
            Chiama
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-[#5D1822] hover:text-[#3A0F15] mb-6"
          data-testid="back-link"
        >
          <ArrowLeft size={20} />
          Indietro
        </Link>

        <div className="mb-8">
          <h2 className="text-4xl md:text-5xl font-serif font-bold text-[#5D1822] mb-4" data-testid="page-title">
            Le Nostre Offerte
          </h2>
          <p className="text-lg text-[#5A5A5A]">
            Scopri le promozioni attive e risparmia sulla carne di qualità
          </p>
        </div>

        {offers.length === 0 ? (
          <div className="bg-white rounded-xl p-12 card-shadow text-center" data-testid="no-offers">
            <Gift className="w-16 h-16 text-[#E5E5E5] mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-[#2C2C2C] mb-2">Nessuna offerta al momento</h3>
            <p className="text-[#5A5A5A]">Torna presto per scoprire le nostre promozioni!</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {offers.map((offer) => (
              <div 
                key={offer.id} 
                className="bg-white rounded-xl overflow-hidden card-shadow hover:shadow-lg transition-all duration-300 hover:-translate-y-1"
                data-testid={`offer-card-${offer.id}`}
              >
                <div className="bg-gradient-to-br from-[#5D1822] to-[#3A0F15] p-6 text-white">
                  {offer.discount_percentage && (
                    <div className="inline-block bg-[#C5A059] text-[#2C2C2C] px-4 py-1 rounded-full text-sm font-bold mb-3">
                      -{offer.discount_percentage}%
                    </div>
                  )}
                  <h3 className="text-2xl font-serif font-bold mb-2" data-testid="offer-title">
                    {offer.title}
                  </h3>
                </div>
                <div className="p-6">
                  <p className="text-[#5A5A5A] mb-4" data-testid="offer-description">
                    {offer.description}
                  </p>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-[#5A5A5A]">Valido fino al:</span>
                    <span className="font-semibold text-[#5D1822]" data-testid="offer-valid-until">
                      {new Date(offer.valid_until).toLocaleDateString('it-IT', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric'
                      })}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Call to Action */}
        <div className="mt-12 bg-gradient-to-br from-[#5D1822] to-[#3A0F15] rounded-2xl p-8 text-white text-center">
          <h3 className="text-2xl md:text-3xl font-serif font-bold mb-4">
            Vuoi approfittare di queste offerte?
          </h3>
          <p className="text-[#F9F7F2]/80 mb-6">
            Chiamaci o vieni a trovarci in negozio!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={handleCall}
              className="bg-white text-[#5D1822] px-8 py-3 rounded-full font-semibold hover:bg-[#F9F7F2] transition-all flex items-center justify-center gap-2"
              data-testid="cta-call-button"
            >
              <Phone size={20} />
              {settings?.phone_number || '+39 123 456 7890'}
            </button>
            <Link
              to="/login"
              className="bg-[#C5A059] text-[#2C2C2C] px-8 py-3 rounded-full font-semibold hover:bg-[#E5C585] transition-all text-center"
              data-testid="cta-login-button"
            >
              Accedi alla Fidelity Card
            </Link>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-[#2C2C2C] text-white py-12 mt-16">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Beef className="w-6 h-6 text-[#C5A059]" />
            <p className="text-lg font-serif font-bold">
              {settings?.business_name || 'Macelleria Borgese'}
            </p>
          </div>
          <p className="text-[#E5E5E5]/60">
            {settings?.tagline || 'Qualità e tradizione dal 1950'}
          </p>
        </div>
      </footer>
    </div>
  );
}
